import { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Users, Mail, Send } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { PendingOwnerInfo } from '@/utils/excelImport';

interface PendingInvitationsReviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pendingOwners: PendingOwnerInfo[];
  orgId: string;
}

interface InvitationSelection {
  email: string;
  selected: boolean;
  role: 'admin' | 'contributor' | 'viewer';
}

export function PendingInvitationsReviewDialog({
  open,
  onOpenChange,
  pendingOwners,
  orgId,
}: PendingInvitationsReviewDialogProps) {
  const [selections, setSelections] = useState<InvitationSelection[]>(
    pendingOwners.map(po => ({
      email: po.email,
      selected: true,
      role: 'contributor',
    }))
  );
  const [isSending, setIsSending] = useState(false);

  const toggleSelection = (email: string) => {
    setSelections(prev =>
      prev.map(s => (s.email === email ? { ...s, selected: !s.selected } : s))
    );
  };

  const updateRole = (email: string, role: 'admin' | 'contributor' | 'viewer') => {
    setSelections(prev =>
      prev.map(s => (s.email === email ? { ...s, role } : s))
    );
  };

  const selectedCount = selections.filter(s => s.selected).length;

  const handleSendInvitations = async () => {
    const toInvite = selections.filter(s => s.selected);
    if (toInvite.length === 0) {
      onOpenChange(false);
      return;
    }

    setIsSending(true);
    let successCount = 0;
    let errorCount = 0;

    for (const invitation of toInvite) {
      try {
        const { error, data } = await supabase.functions.invoke('send-invitation', {
          body: { 
            email: invitation.email, 
            role: invitation.role,
          },
        });

        // Handle "already invited" as success - invitation already exists
        const alreadyInvited = error?.message?.includes('already been sent') || 
                               (data?.error && data.error.includes('already been sent'));
        
        if (error && !alreadyInvited) throw error;

        // Update the pending assignment status to 'invited'
        await supabase
          .from('pending_owner_assignments')
          .update({ 
            status: 'invited', 
            invited_at: new Date().toISOString(),
            role: invitation.role,
          })
          .eq('org_id', orgId)
          .eq('pending_email', invitation.email);

        successCount++;
      } catch (error) {
        console.error(`Failed to invite ${invitation.email}:`, error);
        errorCount++;
      }
    }

    setIsSending(false);

    if (successCount > 0) {
      toast.success(`Sent ${successCount} invitation(s) successfully`);
    }
    if (errorCount > 0) {
      toast.error(`Failed to send ${errorCount} invitation(s)`);
    }

    onOpenChange(false);
  };

  const handleSkipAll = () => {
    toast.info('Skipped invitations. You can invite users later from Settings.');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Review New Owner Invitations
          </DialogTitle>
          <DialogDescription>
            The following users were assigned as owners but don't have accounts in your organization.
            Select who to invite and their role.
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[400px] pr-4">
          <div className="space-y-4">
            {pendingOwners.map((owner) => {
              const selection = selections.find(s => s.email === owner.email);
              if (!selection) return null;

              return (
                <div
                  key={owner.email}
                  className={`rounded-lg border p-4 transition-colors ${
                    selection.selected ? 'border-primary bg-primary/5' : 'opacity-60'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <Checkbox
                      checked={selection.selected}
                      onCheckedChange={() => toggleSelection(owner.email)}
                      className="mt-1"
                    />
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">{owner.email}</span>
                      </div>
                      
                      <div className="flex flex-wrap gap-1">
                        {owner.entities.slice(0, 5).map((entity, idx) => (
                          <Badge key={idx} variant="secondary" className="text-xs">
                            {entity.type}: {entity.name}
                          </Badge>
                        ))}
                        {owner.entities.length > 5 && (
                          <Badge variant="outline" className="text-xs">
                            +{owner.entities.length - 5} more
                          </Badge>
                        )}
                      </div>

                      {selection.selected && (
                        <div className="flex items-center gap-2 pt-2">
                          <Label className="text-sm text-muted-foreground">Role:</Label>
                          <Select
                            value={selection.role}
                            onValueChange={(value) => updateRole(owner.email, value as any)}
                          >
                            <SelectTrigger className="w-32 h-8">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="admin">Admin</SelectItem>
                              <SelectItem value="contributor">Contributor</SelectItem>
                              <SelectItem value="viewer">Viewer</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </ScrollArea>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="ghost" onClick={handleSkipAll} disabled={isSending}>
            Skip All
          </Button>
          <Button
            onClick={handleSendInvitations}
            disabled={isSending}
            className="gap-2"
          >
            <Send className="h-4 w-4" />
            {isSending
              ? 'Sending...'
              : selectedCount === 0
              ? 'Close'
              : `Invite Selected (${selectedCount})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
